/**
 * Every dynamic URL segment in the app, as a route (RO-3-14).
 *
 * ── What this item is for ──
 *
 * `excellent/app/**` has 83 dynamic segment directories. 41 sit under an `/api/`
 * namespace and are HONO routes — they belong to lane L1 (`excellent/server/
 * routes/**`), and a client route claiming `/api/crm/leads/$id` would be a bug,
 * not a port. The other 42 are page segments, and 41 of those are addressable
 * (`app/directory/[kind]` is a bare parent directory with no `page.tsx`, so Next
 * 404s it and there is nothing to register). Seven were already in the tree by the
 * time this rebased onto `agents/integration` (RO-3-02 / RO-3-11 / RO-3-12):
 * `/agents/goals/$id`, `/agents/o/$uid`, `/agents/workflows/$slug`,
 * `/welcome/$step`, `/database/editor/$table`, `/database/objects/$kind`,
 * `/database/schema/$object`. This file adds the remaining 34.
 *
 * The point is sequencing. Thirteen L3 page items are about to land, and every one
 * of them needs its dynamic segment to already exist with the RIGHT param names.
 * Doing it once here means those agents inherit a tree instead of each inventing
 * `$id` vs `$goalId` vs `$slug` for the same URL — and the param names below are
 * Next's own, verbatim, so a ported page body reads `params.projectSlug` exactly
 * as it did before.
 *
 * ── Two kinds of route live here, and the difference is not cosmetic ──
 *
 * **PORTED (4)** — the segments whose entire Next page body is a redirect this
 * host can decide with no DB and no server: the four legacy per-mode object URLs.
 * (`/welcome/$step` is the same kind of pure redirect, but RO-3-12 already owns
 * it in `welcome.tsx` — re-registering would double-claim the path.) These are
 * finished. They behave exactly as the originals do and no later item needs to
 * revisit them.
 *
 * **PENDING (30)** — real pages owned by an L3 item. The PATH and PARAMS are
 * final; the component is a placeholder. Porting one means replacing its
 * `component` and deleting its entry from `PENDING_SEGMENT_PATHS` — the route
 * definition itself does not move. Three database `$…` segments that were pending
 * on the original ship are already finished by RO-3-11 and live outside this file.
 *
 * A placeholder is a liability if it is quiet, because an unmatched path and a
 * placeholder both return 200 `text/html` from the SPA host and a status check
 * cannot tell them apart (that shape produced false findings on this program
 * already). So the placeholder is LOUD in three ways at once: it renders a panel
 * that says the route is not ported, it carries
 * `data-testid="pending-port-segment"` for browser QA, and every pending path is
 * listed in `PENDING_SEGMENT_PATHS` below so `test-route-tree-parity` can assert
 * the list matches the tree and only ever SHRINKS. A silently-green placeholder
 * is not reachable from here.
 *
 * ── One known, benign divergence, stated rather than discovered later ──
 *
 * `/projects/sections` (two segments) has no `page.tsx` in Next, so it 404s there;
 * here it matches `/projects/$projectSlug` with `projectSlug="sections"` and shows
 * the placeholder. The real page resolves the slug against the DB and calls
 * `notFound()`, so this closes by itself when RO-3-xx ports it. Not worth an API
 * round-trip in a placeholder.
 */

import { useEffect, useRef } from "react";
import { createRoute, redirect } from "@tanstack/react-router";
import {
  HelpArticleBody,
  HelpArticleList,
  HelpBreadcrumb,
  HelpFeedbackBlock,
  PageHeader,
  SettingsPageShell,
  SettingsSection,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { help } from "@excellent/ui/api/contracts/help";
import { rootRoute } from "./__root";
import { hopLeftoverLegacyRoute } from "./agents-unmount-hop";

// ─── PORTED · legacy per-mode object URLs ────────────────────────────────────
//
// Issue 25 pt2 — the five `/<mode>/o/<uid>` URLs consolidated onto the ONE
// canonical `/o/<uid>`, each carrying its origin mode through as `?from=<mode>`
// so the rail lights and the breadcrumb reads correctly. `/agents/o/$uid` is the
// fifth and already lives in `agent-redirects.tsx`; these are its four siblings,
// straight ports of pages whose whole body is `redirect(`/o/${uid}?from=…`)`.
//
// `from` is a SHELL-WIDE param (`shell/search.ts`) read by the mode rail and the
// breadcrumb, so these destinations feed an owner that already exists — this is
// not a route inventing a query key.

// `/work/o/$uid` moved to `work.tsx` (RO-3-03 / #44296) — the Work section owns
// the real redirect; keeping a pending twin here would double-register the path.

/** Port of `app/customers/o/[uid]/page.tsx`. */
export const customersObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customers/o/$uid",
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
});

/** Port of `app/operations/o/[uid]/page.tsx`. */
export const operationsObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/o/$uid",
  beforeLoad: ({ params }) => {
    throw redirect({ to: `/o/${params.uid}?from=operations`, replace: true });
  },
});

/** Port of `app/product/o/[uid]/page.tsx`. */
export const productObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product/o/$uid",
  beforeLoad: ({ params }) => {
    throw redirect({ to: `/o/${params.uid}?from=product`, replace: true });
  },
});

// `/welcome/$step` is owned by `welcome.tsx` (RO-3-12) — do not re-register here.

// ─── PENDING · the canonical object page ─────────────────────────────────────

// `/o/$uid` — real body in object-page.tsx (Agents recovery).
// `/agents/runs/$id` — real body in agent-run-detail.tsx (Agents recovery).
//
// Do not re-register pending twins: double-registration crashes TanStack
// buildRouteTree with "Invariant failed" (RO-10-08).

// `agents/workflows/compiled/$slug` ported by RO-3-02c (#44399) —
// `agent-workflow-compiled.tsx`. The real route is registered from tree.ts
// (not via this file's pending list).

// ─── PENDING · mode section catch-alls (UI-UX-PATTERNS Issue 1a/3) ───────────
//
// `/<mode>/<section>[/<view>]` — the real, name-matching section paths, an
// additive alias over the `?area=` form. TanStack's `$` splat exposes the tail as
// `params._splat`, which is where Next's `params.segments` array arrives (joined).
//
// The `o/$uid` sibling above is MORE SPECIFIC than the splat, so a literal `o`
// segment wins and the object redirect still fires — the same precedence Next's
// file router gave for free, and the reason `work/[...segments]/page.tsx`'s own
// comment says "the object route is more specific and wins over this catch-all".

// `/work/$` moved to `work.tsx` (RO-3-03 / #44296) — ModeSectionRoute body, not
// a pending stub. The other mode catch-alls stay here until their section items.

// `/customers/$` ported by RO-3-04 (#44297) — `customers.tsx`. The real route is
// registered from tree.ts (not via this file's pending list). RO-10-08 interim
// ModePage empty-splat handoff is superseded by the real ModeSectionRoute.

// `/operations/$` ported by RO-3-06 (#44299) — body in operations.tsx.

// `/product/$` is PORTED by RO-3-05 / #44298 in `product.tsx` (ModeSectionRoute).
// The path is final there; do not re-register a placeholder here.

// ─── PORTED · project tiers + section-hosted leaves ──────────────────────────
// RO-3-10 / #44303 owns these bodies in `projects.tsx`. Do not re-register
// placeholders here — tree.ts imports the real routes from `./projects`.

// ─── PENDING · directory, help, hiring ───────────────────────────────────────

// `/directory/$kind/$id` ported by RO-3-06 (#44299) — body in operations.tsx.

function HelpCategoryPage() {
  const { categorySlug } = helpCategoryRoute.useParams();
  const categories = useApi(help.categories.list, { query: {} });
  const articles = useApi(help.articles.list, { query: { category_slug: categorySlug, limit: 200 } });
  const category = categories.data?.categories.find((item) => item.slug === categorySlug);
  const rows = articles.data?.articles ?? [];
  const loading = !categories.data || !articles.data;
  return (
    <main data-route="help-category" className="w-full space-y-6 px-6 py-8">
      <HelpBreadcrumb categoryName={category?.name ?? categorySlug} categorySlug={categorySlug} />
      <PageHeader
        title={category?.name ?? (loading ? "Loading category" : "Category not found")}
        description={category?.description ?? (loading ? "Loading help articles." : "This help category is not available on this node.")}
      />
      <HelpArticleList
        items={rows.map((article) => ({
          id: article.id,
          href: `/help/${categorySlug}/${article.slug}`,
          title: article.title,
          subtitle: article.summary ?? undefined,
        }))}
        emptyMessage={loading ? "Loading articles." : "No published articles in this category."}
      />
    </main>
  );
}

export const helpCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help/$categorySlug",
  component: HelpCategoryPage,
});

function HelpArticlePage() {
  const { categorySlug, articleSlug } = helpArticleRoute.useParams();
  const article = useApi(help.articles.get_by_path, { query: { category_slug: categorySlug, slug: articleSlug } });
  const categories = useApi(help.categories.list, { query: {} });
  const viewed = useRef<number | null>(null);
  const row = article.data;
  const category = categories.data?.categories.find((item) => item.slug === categorySlug);
  useEffect(() => {
    if (!row || viewed.current === row.id) return;
    viewed.current = row.id;
    void apiCall(help.articles.record_view, { body: { article_id: row.id, referrer: document.referrer || null } }).catch(() => undefined);
  }, [row]);
  if (!row) {
    return (
      <main data-route="help-article" className="w-full space-y-6 px-6 py-8">
        <HelpBreadcrumb categoryName={category?.name ?? categorySlug} categorySlug={categorySlug} />
        <PageHeader title={article.error ? "Article not found" : "Loading article"} description={article.error ? "This help article is not available on this node." : "Loading help content."} />
      </main>
    );
  }
  return (
    <main data-route="help-article" className="w-full space-y-6 px-6 py-8">
      <HelpBreadcrumb categoryName={category?.name ?? categorySlug} categorySlug={categorySlug} articleTitle={row.title} />
      <PageHeader title={row.title} description={row.summary ?? undefined} />
      <HelpArticleBody body_md={row.body_md} />
      <HelpFeedbackBlock article_id={row.id} />
    </main>
  );
}

export const helpArticleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help/$categorySlug/$articleSlug",
  component: HelpArticlePage,
});

// `/hiring/jobs/$slug` ported by RO-3-07 (hiring.tsx) — real public careers
// page. Pending twin removed so the path is registered once (RO-10-08).

// ─── PENDING · settings ──────────────────────────────────────────────────────
// The three `/database/**/$…` segments are owned by RO-3-11 (`database.tsx`,
// `database-editor.tsx`, `database-objects.tsx`) — do not re-register here.
//
// RO-11-03 (#44357): the two `/styleguide/**` dynamic segments are gone with
// `excellent/app/styleguide/**`. Archive lives at `docs/design/prototypes/`.

// WP9b / #46621 — real connection detail (not a pending placeholder).
// Mapping + records leaves already live in settings-integrations-pages.tsx.
// WP9c / #46622 owns /settings/repos/$id below (SettingsRepoDetail).
function SettingsConnectionDetail() {
  const { id } = settingsConnectionRoute.useParams();
  return (
    <SettingsPageShell
      title={`Connection ${id}`}
      description="Operate this connected account: mapping, raw records, and the integrations hub."
    >
      <div data-testid="settings-connection-detail" data-connection-id={id}>
        <SettingsSection
          id="operate"
          title="This connection"
          description="Connection detail is mounted on the Vite tree. Mapping and raw records are live leaves."
        >
          <p className="type-body text-muted-foreground">
            Connection id <code>{id}</code>. Sync, schedule, and disconnect
            mutations use{" "}
            <code>/api/settings/integrations/*</code>.
          </p>
          <p className="mt-2 type-body">
            <a
              className="underline"
              href={`/settings/integrations/connections/${id}/mapping`}
            >
              Mapping
            </a>
            {" · "}
            <a
              className="underline"
              href={`/settings/integrations/connections/${id}/records`}
            >
              Raw records
            </a>
            {" · "}
            <a className="underline" href="/settings/integrations">
              All integrations
            </a>
          </p>
        </SettingsSection>
      </div>
    </SettingsPageShell>
  );
}

export const settingsConnectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/connections/$id",
  component: SettingsConnectionDetail,
});

// WP9c / #46622 — real repo detail (not a pending placeholder).
// /settings/repos still redirects to the retained /settings/agents/repos list.
function SettingsRepoDetail() {
  const { id } = settingsRepoRoute.useParams();
  return (
    <SettingsPageShell
      title={`Repository ${id}`}
      description="Operate this tracked git repository from the agents Repositories list."
    >
      <div data-testid="settings-repo-detail" data-repo-id={id}>
        <SettingsSection
          id="operate"
          title="This repository"
          description="Repository detail is mounted on the Vite tree. Confirm and archive stay on the retained list."
        >
          <p className="type-body text-muted-foreground">
            Repository id <code>{id}</code>. Confirm and archive mutations use{" "}
            <code>reposList</code> / <code>repoConfirm</code> / <code>repoArchive</code>{" "}
            on{" "}
            <a className="underline" href="/settings/agents/repos">
              Repositories
            </a>
            .
          </p>
          <p className="mt-2 type-body">
            <a className="underline" href="/settings/agents/repos">
              All repositories
            </a>
          </p>
        </SettingsSection>
      </div>
    </SettingsPageShell>
  );
}

export const settingsRepoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/repos/$id",
  component: SettingsRepoDetail,
});

// ─── The manifests ───────────────────────────────────────────────────────────

/**
 * The 3 segments this item finished. Their Next originals are pure redirects with
 * no server dependency, so there is nothing left for a later item to port.
 * (`/welcome/$step` is the same kind of pure redirect but lives in `welcome.tsx`;
 * `/work/o/$uid` moved to `work.tsx` with RO-3-03.)
 */
export const PORTED_SEGMENT_PATHS: readonly string[] = [
  "/customers/o/$uid",
  "/operations/o/$uid",
  "/product/o/$uid",
  "/o/$uid",
  "/agents/runs/$id",
  // RO-3-05 — body lives in product.tsx; listed so the manifest stays complete.
  "/product/$",
  // RO-3-06 — body lives in operations.tsx; listed so the manifest stays complete.
  "/operations/$",
  "/directory/$kind/$id",
  // RO-3-10 — bodies live in projects.tsx.
  "/projects/$projectSlug",
  "/projects/$projectSlug/$sectionSlug",
  "/projects/$projectSlug/$sectionSlug/$",
  "/projects/sections/$sectionSlug",
  "/projects/$projectSlug/$sectionSlug/applications/$id",
  "/projects/$projectSlug/$sectionSlug/candidates/$id",
  "/projects/$projectSlug/$sectionSlug/goals/$id",
  "/projects/$projectSlug/$sectionSlug/jobs/$slug",
  "/projects/$projectSlug/$sectionSlug/leads/$id",
  "/projects/$projectSlug/$sectionSlug/opportunities/$id",
  "/projects/$projectSlug/$sectionSlug/sequences/$id",
  // WP9b / #46621 — body lives in this file (SettingsConnectionDetail).
  "/settings/integrations/connections/$id",
  // WP9c / #46622 — body lives in this file (SettingsRepoDetail).
  "/settings/repos/$id",
];

/**
 * The segments whose path is final but whose page body is not ported.
 *
 * SHRINK-ONLY. Porting a page deletes its entry here; nothing may ever be added.
 * `test-route-tree-parity` asserts this list equals the placeholder routes
 * actually in the tree, so an entry cannot be dropped to make a gate quiet
 * without also removing the placeholder that made it necessary.
 * RO-11-03 (#44357) dropped the two `/styleguide/**` entries with the routes.
 * RO-3-03 (#44296) dropped `/work/$` (real ModeSectionRoute now in work.tsx).
 * RO-3-10 (#44303) moved the projects tier into projects.tsx.
 * RO-10-08 (#44353) dropped `/agents/qa/$runId` + `/hiring/jobs/$slug` (real
 * bodies in agent-qa-run.tsx / hiring.tsx; pending twins crashed the tree).
 * RO-3-04 (#44297) dropped `/customers/$` (real ModeSectionRoute now in customers.tsx).
 * RO-3-06 (#44299) moved `/operations/$` and `/directory/$kind/$id` into operations.tsx.
 */
export const PENDING_SEGMENT_PATHS: readonly string[] = [
  // `/o/$uid` ported (object-page.tsx) — Agents recovery
  // agents analytics you/projects + you/sessions — ported (#44575 agents-analytics.tsx)
  // agents qa runId — ported (agent-qa-run.tsx); dropped from pending (RO-10-08)
  // `/agents/runs/$id` ported (agent-run-detail.tsx) — Agents recovery
  // customers splat — ported by RO-3-04 (44297); real route in customers.tsx
  // operations splat + directory kind/id — ported by RO-3-06 (44299);
  // real routes in operations.tsx. Both now listed in PORTED_SEGMENT_PATHS.
  // hiring jobs slug — ported (hiring.tsx); dropped from pending (RO-10-08)
  // WP9b / #46621 — connections/$id ported (SettingsConnectionDetail).
  // WP9c / #46622 — repos/$id ported (SettingsRepoDetail).
];

/** Every route this file adds, in one array for `tree.ts` to spread. */
export const dynamicSegmentRoutes = [
  // PORTED
  customersObjectRoute,
  operationsObjectRoute,
  productObjectRoute,
  // objectRoute lives in object-page.tsx
  // agentsRunDetailRoute lives in agent-run-detail.tsx
  // customersSectionsRoute lives in customers.tsx (RO-3-04) — do not re-register
  // operationsSectionsRoute + directoryEntityRoute live in operations.tsx (RO-3-06)
  // productSectionsRoute moved to product.tsx (RO-3-05)
  // project routes moved to projects.tsx (RO-3-10)
  helpCategoryRoute,
  helpArticleRoute,
  // hiringJobRoute moved to hiring.tsx (real body; RO-10-08)
  settingsConnectionRoute,
  settingsRepoRoute,
];
